<template>
  <div class="space-y-6 animate-fade-in">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-white">Lookout</h2>
        <p class="text-slate-400 text-sm mt-1">{{ scopeDescription }}</p>
      </div>
      <div class="flex items-center gap-2 flex-wrap justify-end">
        <template v-if="isAdmin">
          <div class="flex items-center gap-2">
            <label class="text-xs text-slate-400">Scope</label>
            <select
              :value="scopeMode"
              class="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
              @change="handleScopeChange"
            >
              <option value="me">My Activity</option>
              <option value="all">All Activity</option>
              <option value="engineer">By Sales Engineer</option>
            </select>
          </div>
          <div v-if="scopeMode === 'engineer'" class="flex items-center gap-2">
            <label class="text-xs text-slate-400">Engineer</label>
            <select
              :value="selectedEngineerId || ''"
              class="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs min-w-[180px]"
              @change="handleEngineerChange"
            >
              <option value="" disabled>Select engineer</option>
              <option v-for="engineer in engineers" :key="engineer.userId" :value="engineer.userId">
                {{ engineer.name || engineer.email || engineer.userId }}
              </option>
            </select>
          </div>
        </template>
        <button
          type="button"
          class="inline-flex items-center justify-center w-8 h-8 rounded border border-slate-700 bg-slate-900/70 text-slate-300 hover:text-white hover:border-slate-500"
          title="Filters"
          @click="showFilterDrawer = true"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
          </svg>
        </button>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="showFilterDrawer"
        class="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[1px]"
        @click.self="showFilterDrawer = false"
      >
        <transition name="slide-right">
          <aside
            v-if="showFilterDrawer"
            class="absolute right-0 top-0 h-full w-[360px] max-w-[94vw] border-l border-slate-700/70 bg-slate-950/95 p-4 shadow-2xl"
          >
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-white uppercase tracking-wide">Filters</h3>
              <button
                type="button"
                class="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-200 text-xs hover:bg-slate-800"
                @click="showFilterDrawer = false"
              >
                Close
              </button>
            </div>
            <div class="grid grid-cols-1 gap-3">
              <div>
                <label class="block text-[11px] text-slate-400 mb-1 uppercase tracking-wide">Start Date</label>
                <input
                  type="date"
                  :value="filters.startDate || ''"
                  class="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
                  @input="handleFilterChange('startDate', ($event.target as HTMLInputElement).value)"
                />
              </div>
              <div>
                <label class="block text-[11px] text-slate-400 mb-1 uppercase tracking-wide">End Date</label>
                <input
                  type="date"
                  :value="filters.endDate || ''"
                  class="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
                  @input="handleFilterChange('endDate', ($event.target as HTMLInputElement).value)"
                />
              </div>
              <div>
                <label class="block text-[11px] text-slate-400 mb-1 uppercase tracking-wide">Organization</label>
                <select
                  :value="filters.organizationId || ''"
                  class="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
                  @change="handleFilterChange('organizationId', ($event.target as HTMLSelectElement).value)"
                >
                  <option value="">All Organizations</option>
                  <option v-for="org in organizations" :key="org.id" :value="String(org.id)">{{ org.name }}</option>
                </select>
              </div>
              <div>
                <label class="block text-[11px] text-slate-400 mb-1 uppercase tracking-wide">Purpose</label>
                <select
                  :value="filters.purpose || ''"
                  class="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
                  @change="handleFilterChange('purpose', ($event.target as HTMLSelectElement).value)"
                >
                  <option value="">All Purposes</option>
                  <option v-for="purpose in purposeOptions" :key="purpose" :value="purpose">{{ purpose }}</option>
                </select>
              </div>
              <div>
                <label class="block text-[11px] text-slate-400 mb-1 uppercase tracking-wide">Project</label>
                <select
                  :value="filters.projectName || ''"
                  class="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
                  @change="handleFilterChange('projectName', ($event.target as HTMLSelectElement).value)"
                >
                  <option value="">All Projects</option>
                  <option v-for="project in projectOptions" :key="project" :value="project">{{ project }}</option>
                </select>
              </div>
              <div class="pt-2 flex items-center justify-between">
                <button
                  type="button"
                  class="text-xs text-primary-300 underline hover:text-primary-200 disabled:text-slate-600 disabled:no-underline"
                  :disabled="!canClearAny"
                  @click="clearAllFilters"
                >
                  Clear All
                </button>
                <button
                  type="button"
                  class="px-2.5 py-1.5 rounded btn-primary text-xs"
                  @click="showFilterDrawer = false"
                >
                  Apply
                </button>
              </div>
            </div>
          </aside>
        </transition>
      </div>
    </transition>

    <div class="mt-[20px] flex items-center gap-2 flex-wrap">
      <span class="text-xs text-slate-500 uppercase tracking-wide">Active Filters</span>
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-primary-700/40 bg-primary-900/30 text-primary-200 text-xs"
        :disabled="scopeMode === 'me'"
        @click="props.onScopeChange?.('me')"
      >
        Scope: {{ scopeChipLabel }}
        <span v-if="scopeMode !== 'me'" class="text-primary-300">x</span>
      </button>
      <button
        v-if="scopeMode === 'engineer' && selectedEngineerChipLabel"
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-primary-700/40 bg-primary-900/30 text-primary-200 text-xs"
        @click="props.onEngineerChange?.('')"
      >
        Engineer: {{ selectedEngineerChipLabel }}
        <span class="text-primary-300">x</span>
      </button>
      <button
        v-for="chip in activeFilterChips"
        :key="chip.key"
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-700 bg-slate-900/70 text-slate-200 text-xs hover:border-slate-500"
        @click="handleFilterChange(chip.key, '')"
      >
        {{ chip.label }}: {{ chip.value }}
        <span class="text-slate-400">x</span>
      </button>
      <span v-if="!hasAnyActiveChip" class="text-xs text-slate-500">None</span>
      <button
        type="button"
        class="text-xs text-primary-300 underline hover:text-primary-200 disabled:text-slate-600 disabled:no-underline"
        :disabled="!canClearAny"
        @click="clearAllFilters"
      >
        Clear All
      </button>
    </div>

    <div class="flex flex-wrap justify-start gap-y-3 gap-x-10 mt-[20px] mb-[50px]">
      <div
        v-for="stat in stats"
        :key="stat.label"
        class="panel-neo panel-neo--glassier glass-anim rounded-2xl px-3 py-4 w-[160px] min-w-[160px]"
      >
        <p class="text-slate-400 text-sm font-medium">{{ statLabel(stat.label) }}</p>
        <div class="mt-1 flex items-start justify-between gap-2">
          <p class="text-3xl font-bold text-white leading-none">{{ stat.value }}</p>
          <p class="text-xs leading-none pt-[5px] whitespace-nowrap" :class="trendClass(stat.change)">
            {{ formatTrend(stat.change) }} (7d)
          </p>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      <div class="lg:col-span-2">
        <div class="relative z-20 flex items-center justify-between mb-4 px-1 mt-[10px]">
          <h3 class="text-lg font-semibold text-white mt-[8px] ml-[10px]">US Checkouts</h3>
        </div>
        <div class="-mt-[55px]">
          <UsMapPlaceholder />
        </div>
      </div>

      <div class="panel-neo panel-neo--glassier glass-anim rounded-2xl px-3 py-5">
        <h3 class="text-lg font-semibold text-white mb-4">Spotter</h3>
        <div class="space-y-2 mb-4">
          <div
            v-for="signal in spotterSignals"
            :key="signal.id"
            class="rounded-lg border px-3 py-2"
            :class="spotterSignalClass(signal.severity)"
          >
            <div class="flex items-start justify-between gap-2">
              <div>
                <p class="text-sm font-semibold text-white">{{ signal.title }}</p>
                <p class="text-xs text-slate-300 mt-0.5">{{ signal.detail }}</p>
              </div>
              <span
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border"
                :class="spotterBadgeClass(signal.severity)"
              >
                {{ signal.severity }}
              </span>
            </div>
            <button
              v-if="signal.actionLabel"
              type="button"
              class="mt-2 text-xs text-primary-300 underline hover:text-primary-200"
              @click="runSpotterAction(signal.actionKey, signal.id)"
            >
              {{ signal.actionLabel }}
            </button>
          </div>
        </div>
        <router-link
          to="/admin-spotter-criteria"
          class="inline-flex items-center gap-1 text-xs text-primary-300 underline hover:text-primary-200"
        >
          Open Spotter Criteria
        </router-link>
      </div>

      <div class="lg:col-span-2 panel-neo panel-neo--glassier rounded-2xl p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-medium text-slate-300">Recent Activity</h3>
          <span v-if="loading" class="text-xs text-slate-500">Loading…</span>
        </div>
        <div v-if="!recentActivity?.length" class="text-slate-500 text-sm py-6 text-center">
          No activity found for this scope.
        </div>
        <div v-else class="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          <div
            v-for="entry in recentActivity"
            :key="entry.id"
            class="rounded-lg border border-slate-800/70 bg-slate-900/35 px-3 py-2"
          >
            <div class="flex items-center justify-between gap-4">
              <div>
                <p class="text-sm text-white font-medium">{{ entry.equipment }}</p>
                <p class="text-xs text-slate-400">{{ entry.action }} • {{ entry.user }}</p>
              </div>
              <div class="text-right">
                <p class="text-xs text-slate-300">{{ formatTime(entry.timestamp) }}</p>
                <p class="text-[11px] text-slate-500 uppercase tracking-wide">{{ entry.status }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="showSpotterDetailsModal"
        class="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
        @click.self="showSpotterDetailsModal = false"
      >
        <div class="w-full max-w-5xl rounded-xl border border-slate-700/80 bg-slate-950/95 p-4 shadow-2xl">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-base font-semibold text-white">{{ spotterDetailsTitle }}</h4>
            <button
              type="button"
              class="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-200 text-xs hover:bg-slate-800"
              @click="showSpotterDetailsModal = false"
            >
              Close
            </button>
          </div>
          <div v-if="!filteredSpotterDetails.length" class="text-sm text-slate-400 py-8 text-center">
            No checkout rows match this signal for the current scope.
          </div>
          <div v-else class="max-h-[420px] overflow-auto rounded-lg border border-slate-800/70">
            <table class="w-full text-sm">
              <thead class="sticky top-0 bg-slate-900 text-slate-300 text-xs uppercase tracking-wide">
                <tr>
                  <th class="px-3 py-2 text-left">Checkout</th>
                  <th class="px-3 py-2 text-left">Equipment</th>
                  <th class="px-3 py-2 text-left">Owner</th>
                  <th class="px-3 py-2 text-left">Project</th>
                  <th class="px-3 py-2 text-left">Status</th>
                  <th class="px-3 py-2 text-left">Outbound Legs</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in filteredSpotterDetails"
                  :key="`${row.checkoutId}-${row.serial}`"
                  class="border-t border-slate-800/70 text-slate-200"
                >
                  <td class="px-3 py-2 font-mono text-xs">#{{ row.checkoutId }}</td>
                  <td class="px-3 py-2">
                    <p class="font-medium text-white">{{ row.equipment }}</p>
                    <p class="text-xs text-slate-500">{{ row.serial }}</p>
                  </td>
                  <td class="px-3 py-2">{{ row.owner }}</td>
                  <td class="px-3 py-2">{{ row.projectName || '—' }}</td>
                  <td class="px-3 py-2">{{ row.status }}</td>
                  <td class="px-3 py-2">{{ row.outboundLegs }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import UsMapPlaceholder from './UsMapPlaceholder.vue'
import { useAuth0Store } from '@/stores/auth0'

type DashboardScopeMode = 'me' | 'all' | 'engineer'
type DashboardFilters = {
  startDate: string
  endDate: string
  organizationId: string
  purpose: string
  projectName: string
}
type SpotterDetailRow = {
  checkoutId: number
  equipment: string
  serial: string
  owner: string
  status: string
  projectName: string
  watchlist: boolean
  outboundLegs: number
  hasOutboundInTransit: boolean
  dispatchGap: boolean
}

const props = defineProps<{
  stats: Array<{
    label: string
    value: number | string
    change: number
    color: string
    icon?: string
    iconSrc?: string
  }>
  recentActivity?: Array<any>
  scopeMode?: DashboardScopeMode
  selectedEngineerId?: string
  engineers?: Array<{ userId: string; name: string | null; email: string | null }>
  filters: DashboardFilters
  organizations?: Array<{ id: number; name: string }>
  purposeOptions?: string[]
  projectOptions?: string[]
  spotterDetails?: SpotterDetailRow[]
  loading?: boolean
  onScopeChange?: (scope: DashboardScopeMode) => void
  onEngineerChange?: (engineerId: string) => void
  onFiltersChange?: (filters: DashboardFilters) => void
  quickActions: Array<{
    name: string
    description: string
    icon: string
    color?: string
    handler: () => void
  }>
}>()

const auth0Store = useAuth0Store()
const showFilterDrawer = ref(false)

const isAdmin = computed(() => auth0Store.isAdmin)
const displayName = computed(() => auth0Store.displayName)
const userRoles = computed(() => auth0Store.userRoles)
const scopeMode = computed<DashboardScopeMode>(() => props.scopeMode || 'me')
const selectedEngineerId = computed(() => props.selectedEngineerId || '')
const engineers = computed(() => props.engineers || [])
const filters = computed<DashboardFilters>(() => props.filters)
const organizations = computed(() => props.organizations || [])
const purposeOptions = computed(() => props.purposeOptions || ['poc', 'test', 'tech_eval', 'maintenance'])
const projectOptions = computed(() => props.projectOptions || [])
const spotterDetails = computed<SpotterDetailRow[]>(() => props.spotterDetails || [])
const loading = computed(() => Boolean(props.loading))
const scopeChipLabel = computed(() => {
  if (scopeMode.value === 'all') return 'All Activity'
  if (scopeMode.value === 'engineer') return 'By Sales Engineer'
  return 'My Activity'
})
const selectedEngineerChipLabel = computed(() => {
  const selected = engineers.value.find((e) => e.userId === selectedEngineerId.value)
  return selected?.name || selected?.email || selectedEngineerId.value || ''
})
const activeFilterChips = computed(() => {
  const chips: Array<{ key: keyof DashboardFilters; label: string; value: string }> = []
  if (filters.value.startDate) chips.push({ key: 'startDate', label: 'Start', value: filters.value.startDate })
  if (filters.value.endDate) chips.push({ key: 'endDate', label: 'End', value: filters.value.endDate })
  if (filters.value.organizationId) {
    const org = organizations.value.find((o) => String(o.id) === filters.value.organizationId)
    chips.push({
      key: 'organizationId',
      label: 'Organization',
      value: org?.name || filters.value.organizationId,
    })
  }
  if (filters.value.purpose) chips.push({ key: 'purpose', label: 'Purpose', value: filters.value.purpose })
  if (filters.value.projectName) chips.push({ key: 'projectName', label: 'Project', value: filters.value.projectName })
  return chips
})
const hasAnyActiveChip = computed(() => {
  if (activeFilterChips.value.length) return true
  if (scopeMode.value !== 'me') return true
  return false
})
const canClearAny = computed(() => hasAnyActiveChip.value)
type SpotterSeverity = 'critical' | 'high' | 'medium' | 'fyi'
type SpotterActionKey = 'details' | ''
type SpotterSignal = {
  id: string
  title: string
  detail: string
  severity: SpotterSeverity
  actionLabel: string
  actionKey: SpotterActionKey
}
const showSpotterDetailsModal = ref(false)
const activeSpotterSignalId = ref('')
function statCount(label: string): number {
  const stat = props.stats.find((s) => s.label === label)
  const raw = Number(stat?.value)
  return Number.isFinite(raw) ? raw : 0
}
const spotterSignals = computed<SpotterSignal[]>(() => {
  const activeCheckouts = statCount('Active Checkouts')
  const maintenance = statCount('Maintenance')
  const dispatchGapCount = spotterDetails.value.filter((row) => Boolean(row.dispatchGap)).length
  const signals: SpotterSignal[] = [
    {
      id: 'dispatch-gap',
      title: 'Dispatch gap',
      detail:
        dispatchGapCount > 0
          ? `${dispatchGapCount} active checkouts have 0 outbound legs.`
          : 'No active dispatch gaps detected in this scope.',
      severity: dispatchGapCount > 0 && activeCheckouts > 0 ? 'high' : 'fyi',
      actionLabel: 'Details',
      actionKey: 'details',
    },
    {
      id: 'maintenance-hold',
      title: 'Maintenance holding inventory',
      detail:
        maintenance > 0
          ? `${maintenance} units are in maintenance.`
          : 'No units are currently blocked in maintenance.',
      severity: 'fyi',
      actionLabel: 'Details',
      actionKey: 'details',
    },
  ]
  const priorityOrder: Record<SpotterSeverity, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    fyi: 3,
  }
  return signals.sort((a, b) => priorityOrder[a.severity] - priorityOrder[b.severity])
})
const spotterDetailsTitle = computed(() => {
  if (activeSpotterSignalId.value === 'dispatch-gap') return 'Dispatch Gap Details'
  if (activeSpotterSignalId.value === 'maintenance-hold') return 'Maintenance Details'
  return 'Spotter Details'
})
const filteredSpotterDetails = computed(() => {
  if (activeSpotterSignalId.value === 'dispatch-gap') {
    return spotterDetails.value.filter((row) => Boolean(row.dispatchGap))
  }
  if (activeSpotterSignalId.value === 'maintenance-hold') {
    return spotterDetails.value.filter((row) => row.status === 'maintenance')
  }
  return spotterDetails.value
})

const scopeDescription = computed(() => {
  if (!isAdmin.value) return 'Your reservations, checkouts, and shipment activity'
  switch (scopeMode.value) {
    case 'all':
      return 'Real-time activity across all sales engineers'
    case 'engineer': {
      const selected = engineers.value.find((e) => e.userId === selectedEngineerId.value)
      const label = selected?.name || selected?.email || 'selected engineer'
      return `Real-time activity for ${label}`
    }
    default:
      return 'Your reservations, checkouts, and shipment activity'
  }
})

function handleScopeChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value as DashboardScopeMode
  props.onScopeChange?.(value)
}

function handleEngineerChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  props.onEngineerChange?.(value)
}

function handleFilterChange(field: keyof DashboardFilters, value: string) {
  props.onFiltersChange?.({
    ...filters.value,
    [field]: value,
  })
}

function clearAllFilters() {
  if (scopeMode.value !== 'me') props.onScopeChange?.('me')
  if (selectedEngineerId.value) props.onEngineerChange?.('')
  props.onFiltersChange?.({
    startDate: '',
    endDate: '',
    organizationId: '',
    purpose: '',
    projectName: '',
  })
}

function runSpotterAction(actionKey: SpotterActionKey, signalId = '') {
  activeSpotterSignalId.value = signalId
  if (actionKey === 'details') {
    showSpotterDetailsModal.value = true
  }
}

function spotterSignalClass(severity: SpotterSeverity) {
  if (severity === 'critical') return 'border-rose-500/40 bg-rose-950/30'
  if (severity === 'high') return 'border-amber-500/40 bg-amber-950/20'
  if (severity === 'medium') return 'border-blue-500/40 bg-blue-950/20'
  return 'border-cyan-500/40 bg-cyan-950/20'
}

function spotterBadgeClass(severity: SpotterSeverity) {
  if (severity === 'critical') return 'border-rose-400/40 text-rose-300 bg-rose-900/30'
  if (severity === 'high') return 'border-amber-400/40 text-amber-300 bg-amber-900/30'
  if (severity === 'medium') return 'border-blue-400/40 text-blue-300 bg-blue-900/30'
  return 'border-cyan-400/40 text-cyan-300 bg-cyan-900/30'
}

function formatTrend(change: number) {
  if (!Number.isFinite(change)) return '+0.0%'
  const sign = change >= 0 ? '+' : ''
  return `${sign}${change.toFixed(1)}%`
}

function trendClass(change: number) {
  if (change > 0) return 'text-emerald-300'
  if (change < 0) return 'text-rose-300'
  return 'text-slate-500'
}

/**
 * Map stat labels based on the current user's role.
 * Admins see fleet-wide labels; regular users see personalized ones.
 */
function statLabel(label: string): string {
  return label
}

function formatTime(timestamp: Date | string) {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp)
  return d.toLocaleString()
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.22s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}
</style>
